import { faqs, instructors } from '../components/landing/landingData'

/**
 * Presentation / identity notes copied from landing and register UI.
 * Not the advisor source of truth.
 * Admissions facts: backend academy knowledge.
 * Commercial course data: PostgreSQL.
 * Educational course information: published course knowledge.
 * Live DB records (enrollments, sessions, masterclass rows) are referenced, not copied.
 */

export const MISSING = 'missing'

export const academy = {
  name: 'Synergy Multimedia Academy',
  shortName: 'SMA',
  slogan: 'Shaping Futures',
  sloganSource: 'Official SMA brand',
  description: {
    text: "A futuristic creative institution training filmmakers, photographers, designers, AI creators, and digital storytellers for global careers.",
    source: 'CinematicHero.jsx',
  },
  mission: {
    text: 'African talent deserves world-class creative education — not outdated textbooks, but real studios, real mentors, and real industry pathways.',
    source: 'AcademyStory.jsx',
  },
  positioning: {
    text: "Uganda's first AI-native multimedia academy — combining hands-on industry training with future-ready creative technology. Small classes, real equipment, and active practitioners as mentors.",
    source: 'landingData.js FAQ; AcademyStory.jsx',
  },
  pillars: [
    { title: 'Creative Independence', text: 'Build skills to freelance, launch studios, or join global teams on your own terms.', source: 'AcademyStory.jsx' },
    { title: 'Digital Storytelling', text: 'Master narrative across photo, film, design, and social platforms with intention.', source: 'AcademyStory.jsx' },
  ],
  location: {
    street: 'Makerere I - Kubiri',
    city: 'Kampala',
    country: 'Uganda',
    source: 'ContactPage.jsx',
    lessSpecific: { text: 'Kampala, Uganda', source: 'CinematicFooter.jsx, Settings.jsx defaults' },
  },
  contact: {
    phone: { display: '+256 706 739 311', tel: '+256706739311', source: 'Official SMA contact' },
    email: 'synergymultimediaacademy@gmail.com',
    website: 'https://www.synergymultimediaacademy.com',
    websiteDisplay: 'www.synergymultimediaacademy.com',
  },
  hours: {
    weekday: { days: 'Mon–Fri', hours: '9am – 6pm', schema: 'Mo-Fr 09:00-18:00' },
    saturday: { days: 'Sat', hours: '11am – 3pm', schema: 'Sa 11:00-15:00' },
    sunday: { status: 'closed', note: 'Sunday is not an office day.' },
    advisorCopy: null,
    advisorNote: 'Advisor visiting hours come from academy knowledge: weekdays, 9:00 AM to 5:00 PM. Office hours above are presentation/business information, not visiting hours.',
    source: 'Official SMA business information',
  },
  established: {
    year: 2025,
    advisorCopy: 'Synergy Multimedia Academy was founded in 2025.',
    source: 'Official SMA business information',
  },
  social: {
    instagram: 'https://instagram.com/synergymultimediaacademy',
    facebook: 'https://facebook.com/synergymultimediaacademy',
    youtube: 'https://youtube.com/synergymultimediaacademy',
    tiktok: 'https://www.tiktok.com/@synergy.multimedi',
    source: 'CinematicFooter.jsx, index.html sameAs',
  },
}

export const admissions = {
  process: [
    { step: 1, title: 'Submit Application', detail: 'Complete your profile in under 5 minutes', source: 'Register.jsx journeySteps' },
    { step: 2, title: 'Pay Admission Fee', detail: '50,000 UGX registration fee — campus or Mobile Money', source: 'Register.jsx journeySteps; auth.routes.js' },
    { step: 3, title: 'Admissions Review', detail: 'Payment is confirmed and the application is reviewed. Register copy says this usually takes 24–48 hours.', source: 'Register.jsx journeySteps and postSubmitSteps' },
    { step: 4, title: 'Account Activated', detail: 'Applicant receives an email when they can log in. Account stays inactive until admin approval.', source: 'Register.jsx; auth.routes.js is_active=false' },
    { step: 5, title: 'Start Training', detail: 'Join the cohort after the portal is activated.', source: 'Register.jsx journeySteps' },
  ],
  requirements: {
    priorExperience: null,
    helpful: null,
    note: 'Advisor requirements come from academy knowledge. Do not use landing FAQ blanket copy. Course-specific prerequisites come from published course knowledge.',
    source: 'landingData.js FAQ (presentation only; not advisor-authoritative)',
    perCoursePrerequisites: MISSING,
  },
  registrationFee: {
    amount: 50000,
    currency: 'UGX',
    label: 'Admission / registration fee',
    howToPay: ['Visit campus', 'Mobile Money, then contact SMA so Finance can confirm'],
    source: 'auth.routes.js; Register.jsx; emailService.js',
  },
  payment: {
    tuition: 'Course tuition is invoiced after application. Amount comes from the selected course fee.',
    installment: null,
    installmentNote: 'Advisor payment structure comes from academy knowledge: pay as you learn - any amount unlocks lessons in proportion to what is paid, remaining balance during the course. Mobile Money 0763875564.',
    methodsMentionedOnSite: ['campus', 'Mobile Money'],
    methodsInPaymentTable: ['cash', 'card', 'bank_transfer', 'mobile_money'],
    source: 'landingData.js FAQ; Register.jsx; backend payments CHECK constraint',
  },
  durationPolicy: {
    standard: '3 months',
    longest: '6 months',
    advisorCopy: 'Most SMA programs run for about 3 months, while some longer programs can run up to 6 months. The exact duration depends on the course.',
    source: 'Official SMA business information',
  },
  intake: {
    hardcoded: false,
    publicCopy: null,
    applicationCloses: null,
    trainingStarts: null,
    specificStartDates: null,
    daysPerWeek: MISSING,
    instruction: 'Current intake dates come from SMA live system / academy knowledge. Do not hardcode dates here.',
    source: 'academy knowledge ADMISSIONS.json intakes.hardcoded=false',
  },
  schedule: {
    studyTimes: [
      { id: '9am', label: '9:00 AM', period: 'Morning' },
      { id: '12pm', label: '12:00 PM', period: 'Afternoon' },
      { id: '6pm', label: '6:00 PM', period: 'Evening' },
    ],
    source: 'Register.jsx; students.preferred_study_time; migrate.js',
  },
  sessions: {
    note: 'Course sessions (morning / noon / evening, dates, seats) exist in the course_sessions table and are managed in admin. Public visitor-facing session calendars are not published as static content.',
    source: 'migrate.js course_sessions; course.routes.js',
    publicSessionCalendar: MISSING,
  },
}

export const facilities = {
  confirmed: [
    { name: 'Professional cameras', source: 'landingData.js FAQ' },
    { name: 'Lighting rigs', source: 'landingData.js FAQ' },
    { name: 'Editing suites with licensed software', source: 'landingData.js FAQ' },
    { name: 'Studio space', source: 'landingData.js FAQ' },
    { name: 'AI creative tools', source: 'landingData.js FAQ' },
  ],
  visitorCopy: [
    'SMA has the equipment needed for practical training.',
    'Having your own equipment is an added advantage, and it is not mandatory unless a specific course says so.',
  ],
  itemizedInventory: {
    status: MISSING,
    note: 'No confirmed itemized inventory. MOCK_EQUIPMENT is not authoritative.',
  },
}

export const mentors = instructors.map((mentor) => ({
  name: mentor.name,
  role: mentor.role,
  image: mentor.image,
  experience: mentor.experience,
  specialty: mentor.specialty,
  highlights: mentor.highlights,
  social: mentor.social,
  source: 'landingData.js instructors',
}))

export const masterclass = {
  seriesName: 'SMA Monthly Masterclass',
  liveSource: 'GET /api/masterclass/upcoming',
  sessionsHardcoded: false,
  note: 'Live dates, times, and availability come from the Masterclass API only.',
}

export const faq = {
  sourceOfTruth: 'frontend/src/components/landing/landingData.js → faqs',
  items: faqs.map((item) => ({
    question: item.q,
    answer: item.a,
    source: 'landingData.js',
  })),
}

export const pathways = [
  {
    id: 'visual-storytelling',
    title: 'Photography & Film',
    courseCodes: ['PHOTO-101', 'VIDEO-CAM-001', 'VIDEO-CINE-001'],
    source: 'FeaturedPrograms.jsx',
  },
  {
    id: 'design-motion',
    title: 'Design & Motion',
    courseCodes: ['GRAPH-GD-001', 'ANIM-2D-001', 'UIUX-001'],
    source: 'FeaturedPrograms.jsx',
  },
  {
    id: 'ai-multimedia',
    title: 'AI & Multimedia',
    courseCodes: ['AI-TOOLS-001', 'MASTER-MULTI-001'],
    source: 'FeaturedPrograms.jsx',
  },
]

export const leadQualificationShape = {
  interest: null,
  goal: null,
  experience: null,
  topic: null,
  otherQuestion: null,
  recommendedCourseCode: null,
  conversationId: null,
  note: 'Advisor conversation context. Contact details are stored only through the advisor lead API.',
}
